import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Building2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { isMultiBranchFeatureEnabled } from "@/lib/permissions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ALL = "all";
const ReportBranchContext = createContext(null);

function fallbackScope(user) {
  const branchId = user?.default_branch?.id ?? null;
  return {
    value: branchId ? String(branchId) : "",
    setValue: () => {},
    branchId,
    allBranches: false,
    reportParams: branchId ? { branch_id: branchId } : {},
    canQuery: Boolean(branchId),
    branches: user?.branches || [],
  };
}

export function ReportBranchProvider({ children }) {
  const { user } = useAuth();
  const multiBranch = isMultiBranchFeatureEnabled(user);
  const branches = multiBranch ? user?.branches || [] : [];
  const defaultId = user?.default_branch?.id ? String(user.default_branch.id) : "";
  const [value, setValue] = useState(defaultId || (multiBranch ? ALL : ""));

  useEffect(() => {
    if (!multiBranch) {
      if (defaultId && value !== defaultId) setValue(defaultId);
      return;
    }
    if (!defaultId) return;
    if (value !== ALL && !branches.some((b) => String(b.id) === value)) {
      setValue(defaultId);
    }
  }, [defaultId, branches, value, multiBranch]);

  const allBranches = multiBranch && value === ALL;
  const branchId = allBranches ? null : Number(value) || user?.default_branch?.id || null;

  const reportParams = useMemo(() => {
    if (allBranches) return { all_branches: 1 };
    if (branchId) return { branch_id: branchId };
    return {};
  }, [allBranches, branchId]);

  const ctx = useMemo(
    () => ({
      value,
      setValue,
      branchId,
      allBranches,
      reportParams,
      canQuery: allBranches || Boolean(branchId),
      branches,
    }),
    [value, branchId, allBranches, reportParams, branches],
  );

  return <ReportBranchContext.Provider value={ctx}>{children}</ReportBranchContext.Provider>;
}

export function useReportBranch() {
  const ctx = useContext(ReportBranchContext);
  const { user } = useAuth();
  return ctx || fallbackScope(user);
}

export function ReportBranchSelect() {
  const { user } = useAuth();
  const ctx = useContext(ReportBranchContext);
  const multiBranch = isMultiBranchFeatureEnabled(user);
  const branches = user?.branches || [];

  if (!ctx || !multiBranch || branches.length < 2) return null;

  return (
    <Select value={ctx.value} onValueChange={ctx.setValue}>
      <SelectTrigger className="h-9 w-[220px] text-[12.5px]" data-testid="report-branch-scope">
        <Building2 size={14} className="mr-1.5 text-brand-orange shrink-0" />
        <SelectValue placeholder="Branch" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All branches</SelectItem>
        {branches.map((b) => (
          <SelectItem key={b.id} value={String(b.id)}>
            {b.brand_name || b.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
